import { T, useTranslate } from '@tolgee/react';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { CuriLightbulb } from '../../../assets/icons/CuriLightbulb';
import theme from '../../../assets/theme';

export type SubjectPickerOption = {
  value: string;
  displayValue: ReactNode;
};

interface InternationalSubjectPickerProps {
  recommended: SubjectPickerOption[];
  other: SubjectPickerOption[];
  selectedValues: string[] | undefined;
  onToggle: (value: string) => void;
  isSubmitting?: boolean;
  // Personalisation for the callout copy. All optional — when missing
  // the picker falls back to a generic line that still nudges the
  // teacher towards English/Literacy.
  firstName?: string;
  city?: string;
  country?: string;
}

/**
 * Onboarding subject picker for teachers in non-native-English countries.
 *
 * Layout (V2 — Personalized Callout):
 *  - A warm beige callout at the top names the teacher's city and lays
 *    out *why* English / Literacy are recommended (international or L2
 *    English instruction is common outside native-English countries).
 *  - A single flex-wrapped grid of chips below: recommended subjects
 *    come first as larger pills with an orange border + halo + a
 *    "★ Popular here" suffix; secondary subjects (Science, Social
 *    Studies, native-language ELA, Math, Other) follow as muted chips.
 *
 * Selected state: filled orange background + white text, identical
 * across both groups so the multi-select gesture reads the same.
 */
export function InternationalSubjectPicker({
  recommended,
  other,
  selectedValues,
  onToggle,
  isSubmitting,
  firstName,
  city,
  country,
}: InternationalSubjectPickerProps) {
  const { t } = useTranslate();
  const isSelected = (value: string) => !!selectedValues?.includes(value);

  // Always capitalise the first letter of the name in case the profile
  // value was stored lowercase ("ingrid" → "Ingrid"). Leaves the rest of
  // the name untouched so casings like "McRae" stay intact.
  const displayFirstName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : undefined;

  // Title line. Personalised when we know firstName + city, otherwise a
  // graceful fallback that still anchors on the school location.
  //
  // Note: dynamic values go through Tolgee's `{placeholder}` syntax
  // (NOT JS template literals) so the values are re-interpolated on
  // every render. Baking them into the default string with `${city}`
  // would freeze the first-rendered value because Tolgee caches the
  // default per key.
  const title = (() => {
    if (displayFirstName && city) {
      return t(
        'onboarding.intlPicker.title.personalized',
        'Hi {firstName}, are you teaching English or literacy at an international school in {city}?',
        { firstName: displayFirstName, city } as never,
      );
    }
    if (city) {
      return t(
        'onboarding.intlPicker.title.city',
        'Hi there, are you teaching English or literacy at an international school in {city}?',
        { city } as never,
      );
    }
    return t(
      'onboarding.intlPicker.title.generic',
      'Hi there, are you teaching English or literacy at an international school?',
    );
  })();

  // Body line. Voice tuned for the Classic Curator persona: warm,
  // resourceful, fearful of "just another thing". Leads with the *why*
  // (tap English or Literacy to unlock our richest content library) and
  // then names what's in it — ready-to-use lessons, hands-on activities,
  // engaging practice — so it lands as a gift, not extra prep.
  const body = country
    ? t(
        'onboarding.intlPicker.body.country',
        'Teaching English or Literacy in {country} classrooms? Tap one (or both) below to unlock our richest content library: ready-to-use lessons, hands-on activities, and engaging practice your students will love.',
        { country } as never,
      )
    : t(
        'onboarding.intlPicker.body.generic',
        'Teaching English or Literacy outside the US? Tap one (or both) below to unlock our richest content library: ready-to-use lessons, hands-on activities, and engaging practice your students will love.',
      );

  return (
    <Wrapper>
      <Callout>
        <LightbulbWrapper aria-hidden="true">
          <CuriLightbulb width="48" height="48" />
        </LightbulbWrapper>
        <CalloutBody>
          <CalloutTitle>{title}</CalloutTitle>
          <CalloutText>{body}</CalloutText>
        </CalloutBody>
      </Callout>

      <ChipGrid>
        {recommended.map(option => (
          <RecommendedChip
            key={option.value}
            type="button"
            $isSelected={isSelected(option.value)}
            disabled={isSubmitting}
            onClick={() => onToggle(option.value)}
          >
            <ChipLabel>{option.displayValue}</ChipLabel>
            <PopularTag $isSelected={isSelected(option.value)}>
              <Star aria-hidden="true">★</Star>
              <T keyName="onboarding.intlPicker.popular" defaultValue="Popular" />
            </PopularTag>
          </RecommendedChip>
        ))}
        {other.map(option => (
          <Chip
            key={option.value}
            type="button"
            $isSelected={isSelected(option.value)}
            disabled={isSubmitting}
            onClick={() => onToggle(option.value)}
          >
            {option.displayValue}
          </Chip>
        ))}
      </ChipGrid>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Callout = styled.div`
  width: 100%;
  background: #fff7ef; /* var(--curi-landing-beige) */
  border: 1px solid #f0e1c4;
  border-radius: 14px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 12px;
    align-items: flex-start;
  }
`;

const LightbulbWrapper = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.orange};

  svg {
    width: 48px;
    height: 48px;
  }
`;

const CalloutBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CalloutTitle = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: ${theme.colors.black};
  margin-bottom: 4px;
  line-height: 1.35;
`;

const CalloutText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4f4f4f;
  line-height: 1.5;
`;

const ChipGrid = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const baseChip = css`
  border-radius: 999px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const RecommendedChip = styled.button<{ $isSelected: boolean }>`
  ${baseChip};
  padding: 14px 22px;
  font-size: 17px;
  font-weight: 700;
  background: ${({ $isSelected }) =>
    $isSelected ? theme.colors.orange : theme.colors.white};
  color: ${({ $isSelected }) => ($isSelected ? theme.colors.white : theme.colors.black)};
  border: 2px solid ${theme.colors.orange};
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? 'none' : '0 0 0 4px rgba(249, 151, 70, 0.12)'};

  &:hover:not(:disabled) {
    background: ${({ $isSelected }) => ($isSelected ? theme.colors.orangeDark : '#fff')};
    box-shadow: ${({ $isSelected }) =>
      $isSelected ? 'none' : '0 0 0 5px rgba(249, 151, 70, 0.18)'};
  }
`;

const ChipLabel = styled.span``;

const PopularTag = styled.span<{ $isSelected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $isSelected }) =>
    $isSelected ? 'rgba(255, 255, 255, 0.9)' : theme.colors.orangeDark};
`;

const Star = styled.span`
  font-size: 12px;
  line-height: 1;
`;

const Chip = styled.button<{ $isSelected: boolean }>`
  ${baseChip};
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  background: ${({ $isSelected }) =>
    $isSelected ? theme.colors.orange : theme.colors.white};
  color: ${({ $isSelected }) => ($isSelected ? theme.colors.white : theme.colors.black)};
  border: 1.5px solid
    ${({ $isSelected }) => ($isSelected ? theme.colors.orange : '#d6cfc4')};

  &:hover:not(:disabled) {
    background: ${({ $isSelected }) =>
      $isSelected ? theme.colors.orangeDark : theme.colors.background};
    border-color: ${({ $isSelected }) =>
      $isSelected ? theme.colors.orangeDark : '#b89a73'};
  }
`;
